import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Lightbulb } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const suggestionSchema = z.object({
  suggested_name: z.string().trim().min(1, 'Namn krävs').max(200),
  city: z.string().trim().max(100).optional().or(z.literal('')),
  region: z.string().trim().max(100).optional().or(z.literal('')),
  address_line1: z.string().trim().max(200).optional().or(z.literal('')),
  contact_info: z.string().trim().max(300).optional().or(z.literal('')),
  note: z.string().trim().max(1000).optional().or(z.literal('')),
});

type SuggestionForm = z.infer<typeof suggestionSchema>;

const SUGGESTION_TYPES = {
  place: {
    title: 'Föreslå en plats',
    description: 'Hjälp oss hitta fler platser i ditt närområde som borde finnas med på Farmly.',
    icon: MapPin,
    nameLabel: 'Platsens namn',
    namePlaceholder: 'T.ex. Leksands Fäbod',
    noteLabel: 'Varför borde den finnas med?',
    notePlaceholder: 'Berätta gärna vad som gör platsen speciell…',
  },
  tip: {
    title: 'Tipsa oss om en värd',
    description: 'Känner du till en producent eller verksamhet som borde finnas med? Tipsa oss!',
    icon: Lightbulb,
    nameLabel: 'Verksamhetens namn',
    namePlaceholder: 'T.ex. Lindas Gårdsmejeri',
    noteLabel: 'Övrig information',
    notePlaceholder: 'Berätta gärna mer om verksamheten…',
  },
} as const;

export default function SuggestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const type = searchParams.get('type') === 'tip' ? 'tip' : 'place';
  const config = SUGGESTION_TYPES[type];
  const Icon = config.icon;

  const { register, handleSubmit, formState: { errors } } = useForm<SuggestionForm>({
    resolver: zodResolver(suggestionSchema),
    defaultValues: { suggested_name: '', city: '', region: '', address_line1: '', contact_info: '', note: '' },
  });

  async function onSubmit(values: SuggestionForm) {
    // Client-side validation for tip contact_info
    if (type === 'tip' && !values.contact_info?.trim()) {
      toast({ title: 'Kontaktuppgift krävs', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const noteValue = type === 'tip'
        ? `Kontakt: ${values.contact_info}${values.note ? `\n\n${values.note}` : ''}`
        : values.note || null;

      const { error } = await supabase
        .from('place_suggestions')
        .insert({
          suggestion_type: type as string,
          suggested_name: values.suggested_name,
          city: values.city || null,
          region: values.region || null,
          address_line1: type === 'place' ? (values.address_line1 || null) : null,
          note: noteValue,
          producer_id: null as any,
        });

      if (error) throw error;

      toast({
        title: 'Tack för ditt förslag!',
        description: 'Vi tar en titt och återkommer om vi behöver mer information.',
      });
      navigate('/app/customer');
    } catch (err: any) {
      console.error('Suggestion submit error:', err);
      toast({
        title: 'Något gick fel',
        description: 'Försök igen om en stund.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-xl mx-auto px-4 py-8 sm:py-12">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </button>

        <Card className="shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2.5 mb-1">
              <Icon className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{config.title}</CardTitle>
            </div>
            <CardDescription className="text-sm leading-relaxed">
              {config.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="suggested_name">{config.nameLabel} *</Label>
                <Input
                  id="suggested_name"
                  placeholder={config.namePlaceholder}
                  {...register('suggested_name')}
                />
                {errors.suggested_name && (
                  <p className="text-xs text-destructive">{errors.suggested_name.message}</p>
                )}
              </div>

              {type === 'tip' && (
                <div className="space-y-1.5">
                  <Label htmlFor="contact_info">Kontaktuppgift (e-post, telefon eller Instagram) *</Label>
                  <Input
                    id="contact_info"
                    placeholder="T.ex. linda@gardsmejeriet.se"
                    {...register('contact_info')}
                  />
                </div>
              )}

              {type === 'place' && (
                <div className="space-y-1.5">
                  <Label htmlFor="address_line1">Adress</Label>
                  <Input
                    id="address_line1"
                    placeholder="Gatuadress (valfritt)"
                    {...register('address_line1')}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="city">Ort</Label>
                  <Input id="city" placeholder="T.ex. Leksand" {...register('city')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="region">Region / Landskap</Label>
                  <Input id="region" placeholder="T.ex. Dalarna" {...register('region')} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="note">{config.noteLabel}</Label>
                <Textarea
                  id="note"
                  placeholder={config.notePlaceholder}
                  rows={3}
                  {...register('note')}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Skickar…' : 'Skicka förslag'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
